import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import BrandName from "../components/site/BrandName";

/**
 * Internal Applications Viewer — read-only Phase 1 admin surface.
 *
 * Auth model:
 *  - Key gate on first visit; verified against GET /api/internal/verify.
 *  - Key stored in localStorage (`hfa_internal_key`); every request
 *    sends it via the X-Internal-Key header (never in the URL).
 *  - On any 401 the stored key is auto-cleared and the gate returns.
 *
 * Data model:
 *  - The backend returns the full application document; the field
 *    schema (labels/types/options) is fetched from /api/internal/schema
 *    so this component auto-supports future fields without edits.
 */

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api/internal";
const STORAGE_KEY = "hfa_internal_key";

/* ============================================================ */
/*  Helpers                                                      */
/* ============================================================ */

function getFromPath(obj, path) {
  if (!obj || !path) return undefined;
  const parts = String(path).split(".");
  let cur = obj;
  for (const p of parts) {
    if (cur == null) return undefined;
    cur = cur[p];
  }
  return cur;
}

function formatValue(value, type) {
  if (value === null || value === undefined || value === "") return "—";
  if (type === "bool") return value ? "Yes" : "No";
  if (type === "multi") {
    if (!Array.isArray(value)) return String(value);
    if (value.length === 0) return "—";
    return value.join(", ");
  }
  return String(value);
}

function toDobDate(dob) {
  // dob is DD/MM/YYYY on /apply. Convert to a comparable YYYY-MM-DD.
  if (!dob || typeof dob !== "string") return null;
  const m = dob.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!m) return null;
  return `${m[3]}-${m[2]}-${m[1]}`;
}

function normalizeDateForCompare(value, type) {
  if (!value) return null;
  if (type === "dob") return toDobDate(value);
  if (type === "date") {
    // "2026-08-04" is already comparable; ISO "2026-08-04T…" gets trimmed.
    return String(value).slice(0, 10);
  }
  if (type === "datetime") {
    return String(value).slice(0, 10);
  }
  return null;
}

/* ============================================================ */
/*  Key Gate                                                     */
/* ============================================================ */

function KeyGate({ initialError, onVerified }) {
  const [value, setValue] = useState("");
  const [error, setError] = useState(initialError || "");
  const [checking, setChecking] = useState(false);

  const submit = async (e) => {
    e && e.preventDefault && e.preventDefault();
    const k = value.trim();
    if (!k) {
      setError("Please enter your Internal Access Key.");
      return;
    }
    setChecking(true);
    setError("");
    try {
      await axios.get(`${API}/verify`, { headers: { "X-Internal-Key": k } });
      try { localStorage.setItem(STORAGE_KEY, k); } catch (_) { /* storage unavailable */ }
      onVerified(k);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Invalid access key.";
      setError(detail);
    } finally {
      setChecking(false);
    }
  };

  return (
    <div
      data-testid="internal-gate"
      style={{
        minHeight: "100vh",
        background: "#0A0A0A",
        color: "white",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
        fontFamily:
          "'Manrope', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      }}
    >
      <form
        onSubmit={submit}
        style={{
          width: "100%",
          maxWidth: 460,
          background: "rgba(255,255,255,0.03)",
          border: "1px solid rgba(255,255,255,0.08)",
          borderRadius: 20,
          padding: "40px 32px",
          backdropFilter: "blur(10px)",
        }}
      >
        <div
          style={{
            fontSize: 11,
            letterSpacing: "0.22em",
            textTransform: "uppercase",
            color: "#EA580C",
            fontWeight: 600,
            marginBottom: 12,
          }}
        >
          <BrandName />
        </div>
        <h1
          style={{
            fontSize: 28,
            lineHeight: 1.15,
            fontWeight: 700,
            margin: 0,
            letterSpacing: "-0.02em",
          }}
        >
          Internal Access
        </h1>
        <p style={{ marginTop: 14, marginBottom: 26, color: "rgba(255,255,255,0.6)", fontSize: 14, lineHeight: 1.55 }}>
          Enter your Internal Access Key to view the applications console.
          The key is remembered on this browser after successful sign-in.
        </p>

        <label style={{ display: "block", fontSize: 12, color: "rgba(255,255,255,0.7)", marginBottom: 8 }}>
          Internal Access Key
        </label>
        <input
          data-testid="internal-gate-input"
          type="password"
          autoFocus
          autoComplete="off"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="Paste your key"
          style={{
            width: "100%",
            padding: "12px 14px",
            background: "rgba(0,0,0,0.5)",
            border: "1px solid rgba(255,255,255,0.14)",
            borderRadius: 12,
            color: "white",
            fontSize: 14,
            fontFamily: "inherit",
            outline: "none",
          }}
        />

        {error && (
          <div
            data-testid="internal-gate-error"
            style={{ marginTop: 12, color: "#FCA5A5", fontSize: 13 }}
          >
            {error}
          </div>
        )}

        <button
          data-testid="internal-gate-submit"
          type="submit"
          disabled={checking}
          style={{
            marginTop: 22,
            width: "100%",
            padding: "13px 16px",
            background: checking ? "rgba(234,88,12,0.5)" : "#EA580C",
            border: "none",
            borderRadius: 12,
            color: "white",
            fontSize: 14,
            fontWeight: 600,
            letterSpacing: "0.01em",
            cursor: checking ? "default" : "pointer",
            fontFamily: "inherit",
          }}
        >
          {checking ? "Verifying…" : "Unlock Console"}
        </button>

        <p style={{ marginTop: 22, fontSize: 12, color: "rgba(255,255,255,0.4)", lineHeight: 1.5 }}>
          If you rotate the key on the server, this device will be signed out
          automatically the next time it hits the console.
        </p>
      </form>
    </div>
  );
}

/* ============================================================ */
/*  Filter Panel                                                 */
/* ============================================================ */

function FilterPanel({ schema, filters, setFilters, onClear, resultCount, total }) {
  const bySection = useMemo(() => {
    const map = {};
    const fields = (schema && schema.fields) || [];
    for (const f of fields) {
      if (!map[f.section]) map[f.section] = [];
      map[f.section].push(f);
    }
    return map;
  }, [schema]);

  if (!schema || !schema.fields) return null;

  const setEnum = (path, v) => setFilters((f) => ({ ...f, [path]: v }));
  const toggleMulti = (path, opt) => {
    setFilters((f) => {
      const cur = new Set(f[path] || []);
      if (cur.has(opt)) cur.delete(opt); else cur.add(opt);
      return { ...f, [path]: Array.from(cur) };
    });
  };
  const setBool = (path, v) => setFilters((f) => ({ ...f, [path]: v }));
  const setText = (path, v) => setFilters((f) => ({ ...f, [path]: v }));
  const setDateRange = (path, key, v) =>
    setFilters((f) => ({ ...f, [`${path}__${key}`]: v }));
  const setNumRange = (path, key, v) =>
    setFilters((f) => ({ ...f, [`${path}__${key}`]: v }));

  return (
    <aside
      data-testid="internal-filter-panel"
      style={{
        width: 320,
        flexShrink: 0,
        borderRight: "1px solid rgba(0,0,0,0.08)",
        background: "#FAFAFA",
        padding: 20,
        overflowY: "auto",
        maxHeight: "calc(100vh - 60px)",
        position: "sticky",
        top: 60,
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <strong style={{ fontSize: 13, letterSpacing: "0.02em" }}>
          Filters
        </strong>
        <button
          data-testid="internal-filter-clear"
          onClick={onClear}
          style={{
            fontSize: 11,
            padding: "4px 10px",
            background: "transparent",
            border: "1px solid rgba(0,0,0,0.15)",
            borderRadius: 999,
            cursor: "pointer",
          }}
        >
          Clear all
        </button>
      </div>
      <div style={{ fontSize: 11, color: "#666", marginBottom: 16 }}>
        Showing {resultCount} of {total}
      </div>

      {schema.sections.map((sec) => (
        <div key={sec} style={{ marginBottom: 22 }}>
          <div
            style={{
              fontSize: 10,
              textTransform: "uppercase",
              letterSpacing: "0.16em",
              color: "#EA580C",
              fontWeight: 700,
              marginBottom: 10,
            }}
          >
            {sec}
          </div>
          {(bySection[sec] || []).map((f) => (
            <FilterRow
              key={f.path}
              field={f}
              filters={filters}
              onEnum={setEnum}
              onMulti={toggleMulti}
              onBool={setBool}
              onText={setText}
              onDateRange={setDateRange}
              onNumRange={setNumRange}
            />
          ))}
        </div>
      ))}
    </aside>
  );
}

function FilterRow({ field, filters, onEnum, onMulti, onBool, onText, onDateRange, onNumRange }) {
  const { path, label, type, options } = field;
  const labelStyle = {
    display: "block",
    fontSize: 11,
    color: "#333",
    marginBottom: 6,
    lineHeight: 1.35,
    fontWeight: 500,
  };
  const boxStyle = { marginBottom: 12 };
  const inputStyle = {
    width: "100%",
    padding: "6px 8px",
    fontSize: 12,
    border: "1px solid rgba(0,0,0,0.15)",
    borderRadius: 6,
    background: "white",
    fontFamily: "inherit",
    outline: "none",
  };

  if (type === "enum") {
    return (
      <div style={boxStyle}>
        <label style={labelStyle}>{label}</label>
        <select
          data-testid={`filter-${path}`}
          value={filters[path] || ""}
          onChange={(e) => onEnum(path, e.target.value)}
          style={inputStyle}
        >
          <option value="">Any</option>
          {(options || []).map((opt) => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      </div>
    );
  }
  if (type === "multi") {
    const active = new Set(filters[path] || []);
    return (
      <div style={boxStyle}>
        <label style={labelStyle}>{label}</label>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
          {(options || []).map((opt) => {
            const on = active.has(opt);
            return (
              <button
                key={opt}
                data-testid={`filter-${path}-${opt}`}
                onClick={() => onMulti(path, opt)}
                type="button"
                style={{
                  fontSize: 11,
                  padding: "3px 8px",
                  border: `1px solid ${on ? "#EA580C" : "rgba(0,0,0,0.15)"}`,
                  background: on ? "#FFEDD5" : "white",
                  color: on ? "#9A3412" : "#333",
                  borderRadius: 999,
                  cursor: "pointer",
                  fontFamily: "inherit",
                }}
              >
                {opt}
              </button>
            );
          })}
        </div>
      </div>
    );
  }
  if (type === "bool") {
    const v = filters[path];
    return (
      <div style={boxStyle}>
        <label style={labelStyle}>{label}</label>
        <select
          data-testid={`filter-${path}`}
          value={v === undefined ? "" : String(v)}
          onChange={(e) => onBool(path, e.target.value === "" ? undefined : e.target.value === "true")}
          style={inputStyle}
        >
          <option value="">Any</option>
          <option value="true">Yes</option>
          <option value="false">No</option>
        </select>
      </div>
    );
  }
  if (type === "date" || type === "datetime" || type === "dob") {
    return (
      <div style={boxStyle}>
        <label style={labelStyle}>{label}</label>
        <div style={{ display: "flex", gap: 6 }}>
          <input
            data-testid={`filter-${path}-from`}
            type="date"
            value={filters[`${path}__from`] || ""}
            onChange={(e) => onDateRange(path, "from", e.target.value)}
            style={inputStyle}
          />
          <input
            data-testid={`filter-${path}-to`}
            type="date"
            value={filters[`${path}__to`] || ""}
            onChange={(e) => onDateRange(path, "to", e.target.value)}
            style={inputStyle}
          />
        </div>
      </div>
    );
  }
  if (type === "number") {
    return (
      <div style={boxStyle}>
        <label style={labelStyle}>{label}</label>
        <div style={{ display: "flex", gap: 6 }}>
          <input
            data-testid={`filter-${path}-min`}
            type="number"
            placeholder="Min"
            value={filters[`${path}__min`] || ""}
            onChange={(e) => onNumRange(path, "min", e.target.value)}
            style={inputStyle}
          />
          <input
            data-testid={`filter-${path}-max`}
            type="number"
            placeholder="Max"
            value={filters[`${path}__max`] || ""}
            onChange={(e) => onNumRange(path, "max", e.target.value)}
            style={inputStyle}
          />
        </div>
      </div>
    );
  }
  // string
  return (
    <div style={boxStyle}>
      <label style={labelStyle}>{label}</label>
      <input
        data-testid={`filter-${path}`}
        value={filters[path] || ""}
        onChange={(e) => onText(path, e.target.value)}
        placeholder="Contains…"
        style={inputStyle}
      />
    </div>
  );
}

/* ============================================================ */
/*  Client-side filter/search evaluation                         */
/* ============================================================ */

function applyFilters(rows, schema, filters, globalQ) {
  if (!schema || !schema.fields) return rows;
  const q = (globalQ || "").trim().toLowerCase();

  const searchable = schema.fields.filter((f) => f.searchable).map((f) => f.path);
  searchable.push("application_id", "id"); // canonical identifiers

  return rows.filter((r) => {
    // Global search across searchable fields
    if (q) {
      let matched = false;
      for (const p of searchable) {
        const v = getFromPath(r, p);
        if (v && String(v).toLowerCase().includes(q)) {
          matched = true;
          break;
        }
      }
      if (!matched) return false;
    }

    for (const f of schema.fields) {
      const { path, type } = f;
      const raw = getFromPath(r, path);

      if (type === "enum") {
        const want = filters[path];
        if (want && String(raw || "") !== want) return false;
      } else if (type === "multi") {
        const want = filters[path];
        if (Array.isArray(want) && want.length > 0) {
          const arr = Array.isArray(raw) ? raw : [];
          for (const w of want) {
            if (!arr.includes(w)) return false;
          }
        }
      } else if (type === "bool") {
        const want = filters[path];
        if (want !== undefined) {
          if (!!raw !== want) return false;
        }
      } else if (type === "date" || type === "datetime" || type === "dob") {
        const from = filters[`${path}__from`];
        const to = filters[`${path}__to`];
        if (from || to) {
          const cmp = normalizeDateForCompare(raw, type);
          if (!cmp) return false;
          if (from && cmp < from) return false;
          if (to && cmp > to) return false;
        }
      } else if (type === "number") {
        const min = filters[`${path}__min`];
        const max = filters[`${path}__max`];
        if (min !== "" && min !== undefined) {
          const n = Number(raw);
          if (isNaN(n) || n < Number(min)) return false;
        }
        if (max !== "" && max !== undefined) {
          const n = Number(raw);
          if (isNaN(n) || n > Number(max)) return false;
        }
      } else {
        // string
        const want = (filters[path] || "").trim().toLowerCase();
        if (want) {
          if (!raw || !String(raw).toLowerCase().includes(want)) return false;
        }
      }
    }
    return true;
  });
}

/* ============================================================ */
/*  Detail Pane                                                  */
/* ============================================================ */

function DetailPane({ record, schema, onClose }) {
  const bySection = useMemo(() => {
    const map = {};
    const fields = (schema && schema.fields) || [];
    for (const f of fields) {
      if (!map[f.section]) map[f.section] = [];
      map[f.section].push(f);
    }
    return map;
  }, [schema]);

  if (!record) return null;
  if (record.loading) {
    return (
      <div style={overlayStyle}>
        <div style={paneStyle}>
          <div style={{ padding: 40, textAlign: "center", color: "#666" }}>Loading…</div>
        </div>
      </div>
    );
  }
  if (record.error) {
    return (
      <div style={overlayStyle}>
        <div style={paneStyle}>
          <PaneHeader title="Error" onClose={onClose} />
          <div style={{ padding: 20, color: "crimson" }}>{record.error}</div>
        </div>
      </div>
    );
  }
  const app = record.application || {};
  const payments = record.payments || [];

  const sections = (schema && schema.sections) || [];

  return (
    <div style={overlayStyle} data-testid="internal-detail-pane">
      <div style={paneStyle}>
        <PaneHeader
          title={app.full_name || app.application_id || "Application"}
          subtitle={app.application_id ? `${app.application_id} · ${app.email || ""}` : app.email || ""}
          onClose={onClose}
        />
        <div style={{ padding: "20px 26px 30px", overflowY: "auto", flex: 1 }}>
          {sections.map((sec) => (
            <section key={sec} style={{ marginBottom: 28 }}>
              <h3
                style={{
                  fontSize: 11,
                  textTransform: "uppercase",
                  letterSpacing: "0.18em",
                  color: "#EA580C",
                  fontWeight: 700,
                  margin: "0 0 12px",
                }}
              >
                {sec}
              </h3>
              <dl style={{ margin: 0 }}>
                {(bySection[sec] || []).map((f) => (
                  <FieldRow key={f.path} field={f} value={getFromPath(app, f.path)} />
                ))}
              </dl>
            </section>
          ))}

          {payments.length > 0 && (
            <section>
              <h3
                style={{
                  fontSize: 11,
                  textTransform: "uppercase",
                  letterSpacing: "0.18em",
                  color: "#EA580C",
                  fontWeight: 700,
                  margin: "0 0 12px",
                }}
              >
                Payment Records ({payments.length})
              </h3>
              {payments.map((p, idx) => (
                <div
                  key={p.id || idx}
                  style={{
                    border: "1px solid rgba(0,0,0,0.08)",
                    background: "#FAFAFA",
                    borderRadius: 10,
                    padding: 14,
                    marginBottom: 10,
                    fontSize: 12,
                    lineHeight: 1.6,
                  }}
                >
                  <div><strong>Status:</strong> {p.status || "—"}</div>
                  <div><strong>Razorpay Order ID:</strong> {p.razorpay_order_id || "—"}</div>
                  <div><strong>Razorpay Payment ID:</strong> {p.razorpay_payment_id || "—"}</div>
                  <div><strong>Amount (paise):</strong> {p.amount_paise ?? "—"}</div>
                  <div><strong>Method:</strong> {p.method || "—"}</div>
                  <div><strong>Created:</strong> {p.created_at || "—"}</div>
                  <div><strong>Paid At:</strong> {p.paid_at || "—"}</div>
                  {p.error_description && (
                    <div style={{ color: "#9A3412" }}><strong>Error:</strong> {p.error_description}</div>
                  )}
                  {p.webhook_event_ids && p.webhook_event_ids.length > 0 && (
                    <div><strong>Webhook Event IDs:</strong> {p.webhook_event_ids.join(", ")}</div>
                  )}
                  {p.refunds && p.refunds.length > 0 && (
                    <div><strong>Refunds:</strong> {JSON.stringify(p.refunds)}</div>
                  )}
                </div>
              ))}
            </section>
          )}
        </div>
      </div>
    </div>
  );
}

function FieldRow({ field, value }) {
  const rendered = formatValue(value, field.type);
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "1fr",
        padding: "10px 0",
        borderBottom: "1px solid rgba(0,0,0,0.05)",
      }}
    >
      <dt
        style={{
          fontSize: 12,
          color: "#666",
          fontWeight: 500,
          lineHeight: 1.5,
          marginBottom: 4,
        }}
      >
        {field.label}
      </dt>
      <dd
        style={{
          margin: 0,
          fontSize: 14,
          color: "#111",
          lineHeight: 1.5,
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
        }}
        data-testid={`detail-${field.path}`}
      >
        {rendered}
      </dd>
    </div>
  );
}

function PaneHeader({ title, subtitle, onClose }) {
  return (
    <div
      style={{
        padding: "20px 26px",
        borderBottom: "1px solid rgba(0,0,0,0.08)",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: 12,
      }}
    >
      <div>
        <div style={{ fontSize: 18, fontWeight: 700, color: "#111" }}>{title}</div>
        {subtitle && (
          <div style={{ fontSize: 12, color: "#666", marginTop: 4 }}>{subtitle}</div>
        )}
      </div>
      <button
        data-testid="internal-close-detail"
        onClick={onClose}
        style={{
          background: "transparent",
          border: "1px solid rgba(0,0,0,0.15)",
          borderRadius: 8,
          padding: "6px 12px",
          fontSize: 12,
          cursor: "pointer",
          fontFamily: "inherit",
        }}
      >
        Close
      </button>
    </div>
  );
}

const overlayStyle = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.4)",
  zIndex: 100,
  display: "flex",
  justifyContent: "flex-end",
};

const paneStyle = {
  width: "min(720px, 100%)",
  background: "white",
  height: "100vh",
  display: "flex",
  flexDirection: "column",
  boxShadow: "-16px 0 40px rgba(0,0,0,0.15)",
};

/* ============================================================ */
/*  Main viewer                                                  */
/* ============================================================ */

export default function InternalViewer() {
  const [authKey, setAuthKey] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) || ""; } catch (_) { return ""; }
  });
  const [bootChecking, setBootChecking] = useState(!!authKey);
  const [gateError, setGateError] = useState("");

  const [schema, setSchema] = useState(null);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loadingRows, setLoadingRows] = useState(false);
  const [error, setError] = useState("");
  const [globalQ, setGlobalQ] = useState("");
  const [filters, setFilters] = useState({});
  const [selected, setSelected] = useState(null);

  // ----- Boot: validate cached key -----
  useEffect(() => {
    if (!authKey) return;
    let cancelled = false;
    (async () => {
      try {
        await axios.get(`${API}/verify`, { headers: { "X-Internal-Key": authKey } });
        if (!cancelled) setBootChecking(false);
      } catch (err) {
        if (cancelled) return;
        try { localStorage.removeItem(STORAGE_KEY); } catch (_) { /* storage unavailable */ }
        setAuthKey("");
        setBootChecking(false);
        if (err?.response?.status === 401) {
          setGateError("Your saved key is no longer valid. Please sign in again.");
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const authHeaders = useMemo(
    () => ({ headers: { "X-Internal-Key": authKey } }),
    [authKey]
  );

  const handleAuthFailure = useCallback((err) => {
    if (err?.response?.status === 401) {
      try { localStorage.removeItem(STORAGE_KEY); } catch (_) { /* storage unavailable */ }
      setAuthKey("");
      setGateError("Your saved key is no longer valid. Please sign in again.");
      return true;
    }
    return false;
  }, []);

  // ----- Load schema + rows once key is present -----
  const loadAll = useCallback(async () => {
    if (!authKey) return;
    setLoadingRows(true);
    setError("");
    try {
      const [schemaRes, rowsRes] = await Promise.all([
        axios.get(`${API}/schema`, authHeaders),
        axios.get(`${API}/applications`, { ...authHeaders, params: { limit: 500 } }),
      ]);
      setSchema(schemaRes.data);
      setRows(rowsRes.data.rows || []);
      setTotal(rowsRes.data.total || 0);
    } catch (err) {
      if (handleAuthFailure(err)) return;
      setError(err?.response?.data?.detail || "Failed to load applications.");
    } finally {
      setLoadingRows(false);
    }
  }, [authKey, authHeaders, handleAuthFailure]);

  useEffect(() => {
    if (authKey && !bootChecking) loadAll();
  }, [authKey, bootChecking, loadAll]);

  // ----- Detail load -----
  const openDetail = async (identifier) => {
    setSelected({ loading: true });
    try {
      const res = await axios.get(
        `${API}/applications/${encodeURIComponent(identifier)}`,
        authHeaders
      );
      setSelected(res.data);
    } catch (err) {
      if (handleAuthFailure(err)) { setSelected(null); return; }
      setSelected({ error: err?.response?.data?.detail || "Failed to load application." });
    }
  };

  // ----- Logout -----
  const logout = () => {
    try { localStorage.removeItem(STORAGE_KEY); } catch (_) { /* storage unavailable */ }
    setAuthKey("");
    setRows([]);
    setSchema(null);
    setSelected(null);
    setFilters({});
    setGlobalQ("");
    setGateError("");
  };

  // ----- CSV Export (server-generated, limited to visible rows) -----
  const exportCsv = async () => {
    if (!authKey) return;
    try {
      const ids = (visibleRows || []).map((r) => r.id).filter(Boolean);
      if (ids.length === 0) {
        setError("Nothing to export — no rows match the current filters.");
        return;
      }
      const url = `${API}/applications/export.csv`;
      const res = await fetch(url, {
        method: "POST",
        headers: {
          "X-Internal-Key": authKey,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ ids }),
      });
      if (res.status === 401) {
        handleAuthFailure({ response: { status: 401 } });
        return;
      }
      if (!res.ok) {
        setError(`Export failed (HTTP ${res.status}).`);
        return;
      }
      const blob = await res.blob();
      const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      const a = document.createElement("a");
      const objectUrl = URL.createObjectURL(blob);
      a.href = objectUrl;
      a.download = `hfa-applications-${stamp}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(objectUrl);
    } catch (err) {
      setError(err?.message || "Export failed.");
    }
  };

  // ----- Filtered results -----
  const visibleRows = useMemo(() => {
    if (!schema) return rows;
    return applyFilters(rows, schema, filters, globalQ);
  }, [rows, schema, filters, globalQ]);

  // ----- Render -----
  if (!authKey) {
    return <KeyGate initialError={gateError} onVerified={(k) => { setAuthKey(k); setGateError(""); }} />;
  }
  if (bootChecking) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", color: "#666", fontFamily: "system-ui" }}>
        Verifying access…
      </div>
    );
  }

  return (
    <div
      style={{
        fontFamily:
          "'Manrope', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        color: "#111",
        background: "white",
        minHeight: "100vh",
      }}
    >
      {/* Top Bar */}
      <header
        style={{
          height: 60,
          borderBottom: "1px solid rgba(0,0,0,0.08)",
          padding: "0 20px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          background: "white",
          position: "sticky",
          top: 0,
          zIndex: 30,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ fontSize: 11, letterSpacing: "0.22em", textTransform: "uppercase", color: "#EA580C", fontWeight: 700 }}>
            <BrandName />
          </div>
          <div style={{ fontSize: 14, fontWeight: 600 }}>Internal Applications Viewer</div>
          <div style={{ fontSize: 12, color: "#888" }}>· MongoDB source of truth</div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <input
            data-testid="internal-search"
            placeholder="Search: name / email / phone / app id / any text field"
            value={globalQ}
            onChange={(e) => setGlobalQ(e.target.value)}
            style={{
              padding: "7px 12px",
              minWidth: 340,
              border: "1px solid rgba(0,0,0,0.15)",
              borderRadius: 8,
              fontSize: 13,
              fontFamily: "inherit",
              outline: "none",
            }}
          />
          <button
            data-testid="internal-refresh"
            onClick={loadAll}
            disabled={loadingRows}
            style={{ padding: "7px 14px", fontSize: 12, border: "1px solid rgba(0,0,0,0.15)", background: "white", borderRadius: 8, cursor: "pointer", fontFamily: "inherit" }}
          >
            {loadingRows ? "Loading…" : "Refresh"}
          </button>
          <button
            data-testid="internal-export-csv"
            onClick={exportCsv}
            style={{ padding: "7px 14px", fontSize: 12, background: "#0A0A0A", color: "white", border: "none", borderRadius: 8, cursor: "pointer", fontWeight: 600, fontFamily: "inherit" }}
          >
            Export CSV
          </button>
          <button
            data-testid="internal-logout"
            onClick={logout}
            title="Clear the saved key on this device"
            style={{ padding: "7px 14px", fontSize: 12, background: "transparent", color: "#666", border: "1px solid rgba(0,0,0,0.15)", borderRadius: 8, cursor: "pointer", fontFamily: "inherit" }}
          >
            Log Out
          </button>
        </div>
      </header>

      {error && (
        <div data-testid="internal-error" style={{ padding: "10px 20px", background: "#FEE2E2", color: "#9A3412", fontSize: 13 }}>
          {error}
        </div>
      )}

      <div style={{ display: "flex", alignItems: "flex-start" }}>
        {schema && (
          <FilterPanel
            schema={schema}
            filters={filters}
            setFilters={setFilters}
            onClear={() => setFilters({})}
            resultCount={visibleRows.length}
            total={total}
          />
        )}

        <main style={{ flex: 1, padding: 20, overflowX: "auto" }}>
          <table
            data-testid="internal-table"
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: 12.5,
              background: "white",
            }}
          >
            <thead>
              <tr style={{ background: "#F5F5F5", textAlign: "left" }}>
                {[
                  "Applied",
                  "App ID",
                  "Full Name",
                  "Email",
                  "WhatsApp",
                  "City Chapter",
                  "Primary Circle",
                  "Status",
                  "Payment",
                  "Membership ID",
                  "",
                ].map((h) => (
                  <th key={h} style={{ padding: "10px 10px", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.08em", color: "#555", borderBottom: "1px solid rgba(0,0,0,0.08)" }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((r) => (
                <tr key={r.id} style={{ borderBottom: "1px solid rgba(0,0,0,0.05)" }}>
                  <td style={{ padding: "10px 10px", whiteSpace: "nowrap" }}>
                    {r.applied_date || "—"} {r.applied_time || ""}
                  </td>
                  <td style={{ padding: "10px 10px", fontFamily: "SFMono-Regular, Menlo, Consolas, monospace", fontSize: 12 }}>{r.application_id || "—"}</td>
                  <td style={{ padding: "10px 10px", fontWeight: 600 }}>{r.full_name || "—"}</td>
                  <td style={{ padding: "10px 10px" }}>{r.email || "—"}</td>
                  <td style={{ padding: "10px 10px" }}>{r.whatsapp_number || "—"}</td>
                  <td style={{ padding: "10px 10px" }}>{r.city_chapter || "—"}</td>
                  <td style={{ padding: "10px 10px", maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.primary_circle || "—"}</td>
                  <td style={{ padding: "10px 10px" }}><StatusPill value={r.status} /></td>
                  <td style={{ padding: "10px 10px" }}><StatusPill value={r.payment_status} kind="payment" /></td>
                  <td style={{ padding: "10px 10px" }}>{r.membership_id || "—"}</td>
                  <td style={{ padding: "10px 10px" }}>
                    <button
                      data-testid={`internal-view-${r.application_id || r.id}`}
                      onClick={() => openDetail(r.application_id || r.id)}
                      style={{ padding: "5px 12px", fontSize: 12, border: "1px solid rgba(0,0,0,0.15)", background: "white", borderRadius: 6, cursor: "pointer", fontFamily: "inherit" }}
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
              {visibleRows.length === 0 && !loadingRows && (
                <tr>
                  <td colSpan="11" style={{ textAlign: "center", padding: "40px 20px", color: "#888" }}>
                    No applications match the current search and filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </main>
      </div>

      {selected && (
        <DetailPane record={selected} schema={schema} onClose={() => setSelected(null)} />
      )}
    </div>
  );
}

function StatusPill({ value, kind }) {
  if (!value) return <span style={{ color: "#888" }}>—</span>;
  const palette = {
    paid: { bg: "#DCFCE7", fg: "#166534" },
    pending_review: { bg: "#FEF3C7", fg: "#92400E" },
    accepted: { bg: "#DBEAFE", fg: "#1E40AF" },
    rejected: { bg: "#FEE2E2", fg: "#991B1B" },
    failed: { bg: "#FEE2E2", fg: "#991B1B" },
    created: { bg: "#F3F4F6", fg: "#374151" },
    refunded: { bg: "#E9D5FF", fg: "#6B21A8" },
  };
  const p = palette[value] || { bg: "#F3F4F6", fg: "#374151" };
  return (
    <span
      style={{
        display: "inline-block",
        padding: "2px 10px",
        borderRadius: 999,
        background: p.bg,
        color: p.fg,
        fontSize: 11,
        fontWeight: 600,
        textTransform: "capitalize",
      }}
    >
      {String(value).replace(/_/g, " ")}
    </span>
  );
}
